import $ from 'jquery';
import apiFetch from '@wordpress/api-fetch'
import {
    getRestRoute,
    getFieldValue,
    submitErrorMessage,
    setFieldValue,
    extractShippingMethod,
    isAdminUser,
} from "@ppcp/utils";
import {isEmpty, isEqual} from 'lodash';
import BaseController from './BaseController';
import {getPaymentMethod} from '@ppcp/payment-methods';
import {getErrorMessage} from "../helpers/utils";

/**
 * Checkout Controller
 *
 * Handles WooCommerce checkout-specific payment flow orchestration.
 * Works with any payment gateway (PayPal, Google Pay, etc.)
 */
class CheckoutController extends BaseController {

    constructor(props) {
        super(props);
        this.shippingAddressChanged = false;
        this.promises = [];
    }

    initialize() {
        super.initialize();
        this.actions.addAction('updated_checkout', this.updatedCheckout.bind(this));
        $(document.body).on('update_checkout', this.updateCheckout.bind(this));

        if (this.context.isCheckout()) {
            $(document.body).on('payment_method_selected', this.paymentMethodSelected.bind(this));
            $('form.checkout').on(`checkout_place_order_${this.id}`, this.onCheckoutPlaceOrder.bind(this));
        } else {
            $(document.body).on('change', 'input[name="payment_method"]', this.paymentMethodSelected.bind(this));
            $(document.body).on('click', '#place_order', this.onClickPlaceOrder.bind(this));
        }

        $(document.body).on('checkout_error', this.onCheckoutError.bind(this));
        $(document.body).on('click', `.wc-${this.gateway.id}-cancel__payment`, this.cancelPayment.bind(this));
        $(document.body).on('change', '[name="terms"]', this.handleTermsClick.bind(this));
        $(document.body).on('change', '[type="checkbox"]', this.handleCheckboxChange.bind(this));
        $(document.body).on('change', `[name="wc-${this.id}-payment-token"]`, this.onPaymentTokenClick.bind(this));
        window.addEventListener('hashchange', this.handleHashError.bind(this));
        this.handleOrderPay();
    }

    async createPaymentElement() {
        await Promise.all(this.promises);
        this.promises = [];
        if (this.context.isCheckout() && !this.isEnabledForCheckoutSection()) {
            this.hidePaymentMethodRow();
            return;
        }
        super.createPaymentElement().then(() => {
            this.paymentMethodSelected();
        });
    }

    isEnabledForCheckoutSection() {
        return this.gateway.settings?.sections?.includes('checkout') ?? true;
    }

    hidePaymentMethodRow() {
        $(`form.checkout li.payment_method_${this.id}`).hide();
    }

    /**
     * Called when payment method is unavailable (e.g., Google Pay or Card Fields not supported)
     * - If browser unsupported: hides the payment method row
     * - If configuration issue: shows an informational message
     */
    onPaymentUnavailable() {
        // If no configuration error, it means browser is unsupported - hide the payment method row
        if (!this.configurationError) {
            this.hidePaymentMethodRow();
            return;
        }

        // Configuration issue - show message (different for admins vs regular users)
        const msg = isAdminUser()
            ? (this.gateway.settings?.i18n?.unavailable_admin || this.configurationError)
            : (this.gateway.settings?.i18n?.unavailable || 'Payment method unavailable');

        const $paymentMethodContainer = $(`div.payment_method_${this.id}`).find(`.wc-${this.id}-payment-method__container`);
        $paymentMethodContainer.find('.woocommerce-info').remove();
        $paymentMethodContainer.prepend(`<div class="woocommerce-info"><p>${msg}</p></div>`);
    }

    needsShipping() {
        if (this.context.isOrderPay() || this.context.isAddPaymentMethod()) {
            return false;
        }
        return this.cart.needsShipping();
    }

    updatedCheckout(data) {
        super.initialize();
        this.updateGatewaySettings(data);
        this.paymentMethodSelected();
        if (this.isOrderReview()) {
            this.displayPaymentReadyMessage();
        } else if (this.isReadyToCheckout()) {
            this.displayPaymentReadyMessage();
        }
    }

    updateCheckout() {
        this.promises.push(
            new Promise(resolve => {
                $(document.body).one('updated_checkout', () => {
                    resolve();
                });
            })
        )
    }

    /**
     * Method that is called when a payment method is selected
     */
    paymentMethodSelected() {
        if (this.isPaymentGatewaySelected()) {
            // If payment method is unavailable, always show the place order button
            if (!this.isPaymentMethodAvailable) {
                this.displayPlaceOrderButton();
                return;
            }

            if (this.gateway.supports('place_order_button')) {
                this.displayPlaceOrderButton();
                if (this.isReadyToCheckout() && this.isPaymentSectionPlacement()) {
                    this.hidePaymentElement();
                }
            } else {
                if (!this.isReadyToCheckout()) {
                    if (!this.isSavedPaymentMethodSelected()) {
                        this.displayPaymentElement();
                        this.hidePlaceOrderButton();
                    } else {
                        this.displayPlaceOrderButton();
                    }
                } else {
                    this.displayPlaceOrderButton();
                }
            }
        } else {
            if (!this.gateway.supports('place_order_button')) {
                this.hidePaymentElement();
                // Check if the newly selected payment method has a registered controller
                // If not registered, show the place order button (no controller will manage it)
                const selectedMethod = $('[name="payment_method"]:checked')?.val();
                if (selectedMethod && !getPaymentMethod(selectedMethod)) {
                    this.displayPlaceOrderButton();
                }
            }
        }
    }

    handleHashError(e) {
        var match = e.newURL.match(/ppcp_error=(.*)/);
        if (match) {
            const {1: error} = match;
            if (error == 'true') {
                this.displayPaymentElement();
                history.pushState({}, '', window.location.pathname + window.location.search);
            }
        }
    }

    isOrderReview() {
        let match = window?.location?.search?.match(/_ppcp_order_review=(.*)/);
        return match?.length > 0;
    }

    handleOrderPay() {
        if (this.isOrderReview()) {
            try {
                let match = window?.location?.search?.match(/_ppcp_order_review=(.*)/);
                const {1: obj} = match;
                const {payment_method, paypal_order, fields} = JSON.parse(atob(decodeURIComponent(obj)));
                setFieldValue(this.order_field_key, paypal_order, '');
                if (!isEmpty(fields)) {
                    for (let key in fields) {
                        setFieldValue(key, fields[key], '');
                    }
                }
                this.hidePaymentElement();
                if (this.needsShipping() && $('[name="ship_to_different_address"]')?.length) {
                    const bool = !isEqual(this.getCartAddress('billing'), this.getCartAddress('shipping'))
                    $('[name="ship_to_different_address"]').prop('checked', bool).trigger('change');
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    /**
     * Create order - checkout-specific implementation
     */
    createOrder(data, actions) {
        if (this.context.isOrderPay()) {
            return this.cart.doOrderPay(this.id);
        } else {
            const formData = {...this.convertFormToData(), context: this.context.getPage(), payment_method: this.id};
            return this.cart.createOrder(formData);
        }
    }

    createVaultSetupToken(formData = {}) {
        if (!this.context.isOrderPay()) {
            formData = {...this.convertFormToData(), context: this.context.getPage(), payment_method: this.id};
        }
        return super.createVaultSetupToken(formData);
    }

    displayPlaceOrderButton() {
        this.getPlaceOrderButton()?.removeClass('wc-ppcp-hide-button');
    }

    hidePlaceOrderButton() {
        this.getPlaceOrderButton()?.addClass('wc-ppcp-hide-button');
    }

    getPlaceOrderButton() {
        let $button = $('#place_order');
        if (!$button.length) {
            $button = $('[name="woocommerce_checkout_place_order"]');
        }
        if (!$button.length) {
            $button = $('form.checkout button[type="submit"]');
        }
        if (!$button.length) {
            $button = $('form[id="order_review"] button[type="submit"]');
        }
        return $button;
    }

    getButtonPlacement() {
        return this.settings?.buttonPlacement || 'place_order';
    }

    isPlaceOrderPlacement() {
        return this.getButtonPlacement() === 'place_order';
    }

    isPaymentSectionPlacement() {
        return this.getButtonPlacement() === 'payment_method';
    }

    getElementContainer() {
        // Return custom selector if provided
        if (this.paymentElementSelector) {
            return document.querySelector(this.paymentElementSelector);
        }

        const placement = this.getButtonPlacement();
        const containerClass = `wc-${this.id}-checkout-container`;
        const containerSelector = `.${containerClass}`;
        const shouldPlaceAfterButton = placement === 'place_order' && !this.gateway.supports('place_order_button');

        // Create container if it doesn't exist
        if (!document.querySelector(containerSelector)) {
            if (shouldPlaceAfterButton) {
                this.getPlaceOrderButton().after(`<div class="${containerClass}"></div>`);
            } else if (placement === 'payment_method') {
                $(`div.payment_method_${this.id}`).find(`.wc-${this.id}-payment-method__container`)
                    .append(`<div class="${containerClass}"></div>`);
            }
        }

        // Hide container for place_order placement (whether new or existing)
        if (shouldPlaceAfterButton) {
            $(containerSelector).hide();
        }

        return document.querySelector(containerSelector);
    }

    isPaymentGatewaySelected() {
        return $('[name="payment_method"]:checked')?.val() === this.id;
    }

    submitError(error) {
        if (error?.code === 'validation_errors') {
            this.getForm().find('.input-text, select, input:checkbox').trigger('validate').trigger('blur');
            return submitErrorMessage(error.data.messages, this.getForm(), 'checkout');
        }
        return submitErrorMessage(error, this.getForm(), 'checkout');
    }

    getShippingPrefix() {
        if ($('[name="ship_to_different_address"]')?.length) {
            if ($('[name="ship_to_different_address"]').is(':checked')) {
                return 'shipping';
            }
        }
        return 'billing';
    }

    handlePaymentToken(token, data) {
        this.update_required = this.isCheckoutReviewRequired(token);
        super.handlePaymentToken(token, {
            vaultSetupToken: true,
            ...data
        });
    }

    isCheckoutReviewRequired(token) {
        if (this.needsShipping() && this.actions.applyFilters('is_checkout_review_required', !this.shippingAddressChanged, this)) {
            if (!isEmpty(token.shipping_address)) {
                if (!isEqual(
                    {
                        city: token.shipping_address.city,
                        state: token.shipping_address.state,
                        postal_code: token.shipping_address.postal_code,
                        country_code: token.shipping_address.country_code
                    },
                    {
                        city: getFieldValue('shipping_city'),
                        state: getFieldValue('shipping_state'),
                        postal_code: getFieldValue('shipping_postcode'),
                        country_code: getFieldValue('shipping_country')
                    }
                )) {
                    return true;
                }
            }
        }
        return false;
    }

    getForm() {
        if (this.context.isCheckout() || this.context.isExpressCheckout()) {
            return $(this.container).closest('form.checkout');
        } else {
            return $(this.container).closest('form');
        }
    }

    validateTerms() {
        if ($('[name="terms"]').filter(':visible').length) {
            return $('[name="terms"]').is(':checked');
        }
        return true;
    }

    onCheckoutPlaceOrder(e, wcCheckoutForm = null) {
        // For saved payment methods, allow submission
        if (this.isSavedPaymentMethodSelected()) {
            return true;
        }

        // Check if payment is already ready (order/token exists from gateway)
        if (this.isReadyToCheckout() || this.gateway.isPaymentReady?.()) {
            return true;
        }

        // Validate WooCommerce checkout fields (terms, etc.)
        if (!this.validateSync()) {
            return false;
        }

        if (this.gateway.supports('place_order_button') && this.isPaymentSectionPlacement()) {
            if (!this.isReadyToCheckout()) {
                this.submitError(getErrorMessage({code: 'order_button_click'}).replace('%s', this.gateway.settings.title));
                return false;
            }
        }

        // Validate payment element (e.g., credit card fields)
        try {
            this.gateway.validatePaymentElement();
        } catch (error) {
            this.submitError(error);
            return false;
        }

        // For credit cards: create payment method and prevent submission
        // The gateway's onApprove will resubmit the form after payment method is created
        if (this.gateway.supports('create_payment_method')) {
            this.gateway.createPaymentMethod(wcCheckoutForm).catch(error => {
                this.submitError(error);
            });
            return false; // Prevent form submission - will be resubmitted by gateway
        }

        // For PayPal/Google Pay: validation passed - allow submission
        return true;
    }

    isTermsVisible() {
        return $('[name="terms"]').filter(':visible').length > 0;
    }

    handleTermsClick() {
        if (this.isPlaceOrderPlacement()) {
            if ($('[name="terms"]').length) {
                const checked = $('[name="terms"]').is(':checked');
                if (checked) {
                    this.enablePaymentElement();
                } else {
                    this.disablePaymentElement();
                }
            }
        }
    }

    handleCheckboxChange() {
        setTimeout(() => {
            if (this.isTermsVisible()) {
                this.handleTermsClick();
            }
        }, 250);
    }

    /**
     * Called when a saved payment method is selected or deselected
     * Handles showing/hiding the payment element based on selection
     */
    onPaymentTokenClick(e) {
        if (this.isSavedPaymentMethodSelected()) {
            this.hidePaymentElement();
            this.displayPlaceOrderButton();
        } else {
            this.displayPaymentElement();
            if (!this.gateway.supports('place_order_button')) {
                this.hidePlaceOrderButton();
            }
        }
    }

    /**
     * Called when place order button is clicked (non-checkout pages like order-pay)
     * For credit cards, prevents default and triggers payment method creation
     */
    onClickPlaceOrder(e) {
        if (this.isPaymentGatewaySelected()) {
            if (!this.isSavedPaymentMethodSelected()) {
                if (this.gateway.supports('create_payment_method')) {
                    e.preventDefault();
                    this.gateway.createPaymentMethod();
                }
            }
        }
    }

    validateSync() {
        if (this.isPlaceOrderPlacement()) {
            if (!this.validateTerms()) {
                this.submitError({code: 'terms'});
                return false;
            }
        }

        // Validate form fields for gateways that require it (e.g., Google Pay on checkout)
        if (this.gateway.supports('form_validation')) {
            const $form = this.getForm();

            // Trigger WooCommerce's built-in validation only on visible fields
            $form.find('.input-text, select, input:checkbox').filter(':visible').trigger('validate').trigger('blur');

            // Check if there are any validation errors on visible form rows
            // (WooCommerce adds woocommerce-invalid to the parent .form-row)
            const $invalidFields = $form.find('.form-row.woocommerce-invalid:visible');

            if (this.actions.applyFilters('checkout_form_validation_has_errors', $invalidFields.length > 0, $invalidFields, this)) {
                // Scroll to first error
                this.submitError({code: 'required_fields'});
                return false;
            }
        }
        return true;
    }

    async validate() {
        if (!this.validateSync()) {
            return false;
        }

        if (this.gateway.supports('api_form_validation')) {
            try {
                await this.apiFormValidation();
            } catch (error) {
                this.submitError(error);
                return false;
            }
        }

        return true;
    }

    onShippingChange(data) {
        let shippingMethod;
        if (data?.selected_shipping_option?.id) {
            shippingMethod = extractShippingMethod(data.selected_shipping_option.id);
            for (let index of Object.keys(shippingMethod)) {
                const method = shippingMethod[index];
                const el = $(`[name="shipping_method[${index}]"][value="${method}"]`);
                if (el.length) {
                    el.prop('checked', true);
                }
            }
        }
        if (data.address) {
            this.shippingAddressChanged = !isEqual(
                data.address,
                {
                    city: getFieldValue('shipping_city'),
                    state: getFieldValue('shipping_state'),
                    postcode: getFieldValue('shipping_postcode'),
                    country: getFieldValue('shipping_country')
                }
            );
        }
        data = {...data, ...this.convertFormToData()};
        this.actions.doAction('checkout_on_shipping_change', data, this);
        return super.onShippingChange(data);
    }

    displayPaymentReadyMessage() {
        $(`.wc-${this.gateway.id}-popup__container`).hide();
        $(`.wc-${this.gateway.id}-order-review-message__container`).show();
        const txt = $(`.wc-${this.gateway.id}-order-review__message`).text().replace('%s', $('#place_order').text());
        $(`.wc-${this.gateway.id}-order-review__message`).text(txt);
    }

    hidePaymentReadyMessage() {
        $(`.wc-${this.gateway.id}-popup__container`).show();
        $(`.wc-${this.gateway.id}-order-review-message__container`).hide();
    }

    cancelPayment(e) {
        e.preventDefault();
        setFieldValue(this.order_field_key, '', '');
        setFieldValue(`${this.gateway.id}_payment_token`, null, null);
        this.hidePaymentReadyMessage();
        this.displayPaymentElement();
        if (!this.gateway.supports('place_order_button')) {
            this.hidePlaceOrderButton();
        }
    }

    getProcessingSelector() {
        return this.container;
    }

    getProcessingMessage() {
        return null;
    }

    fetchBillingToken(token) {
        this.showProcessing();
        return super.fetchBillingToken(token).then(response => {
            this.hideProcessing();
            return response;
        });
    }

    isReadyToCheckout() {
        return !!(getFieldValue(this.order_field_key, null) || getFieldValue(`${this.gateway.id}_payment_token`, null));
    }

    convertFormToData() {
        return $('form.checkout').serializeArray().reduce((prev, current) => ({
            ...prev,
            [current.name]: current.value
        }), {});
    }

    submit(checkoutData = null) {
        // Populate checkout fields if data provided
        if (checkoutData) {
            this.populateCheckoutFields(checkoutData);
        }

        // Only hide payment element for gateways that don't use place order button
        // (e.g., PayPal/Google Pay buttons hide, but credit card forms stay visible)
        if (!this.gateway.supports('create_payment_method')) {
            this.hidePaymentElement();
            this.displayPlaceOrderButton();
        }

        // Handle checkout update flow if shipping address changed
        if (this.update_required) {
            this.triggerCheckoutUpdate(checkoutData);
        } else {
            // No update required, submit directly
            // don't submit the form if the payment element is in the payment section so
            // the customer can complete all fields.
            if (!this.isPaymentSectionPlacement() || this.context.isExpressCheckout()) {
                this.submitForm(checkoutData);
            }
            // Form was not submitted so show customer payment ready message so
            // they know their payment method is ready
            if (this.isPaymentSectionPlacement()) {
                this.displayPaymentReadyMessage();
            }
        }
    }

    /**
     * Trigger WooCommerce checkout update and wait for completion
     * @param {Object} checkoutData - Checkout data containing vault token info
     * @returns {Promise} Promise that resolves when checkout is updated
     */
    triggerCheckoutUpdate(checkoutData) {
        return new Promise((resolve) => {
            $(document.body).one('updated_checkout', () => {
                // If vault setup token and needs shipping, show payment ready message
                if (checkoutData?.vaultSetupToken && this.needsShipping()) {
                    this.displayPaymentReadyMessage();
                } else {
                    // Actually submit the form
                    this.submitForm(checkoutData);
                }
                resolve();
            });

            // Trigger country/state changes to update checkout
            $('[name="billing_country"],[name="billing_state"]').trigger('change');
            if (this.shipToDifferentAddressChecked()) {
                $('[name="shipping_country"],[name="shipping_state"]').trigger('change');
            }
            $(document.body).trigger('update_checkout', {update_shipping_method: false});
        });
    }

    /**
     * Submit the checkout form
     * @param {Object} checkoutData - Optional checkout data for filters
     */
    submitForm(checkoutData) {
        if (this.actions.applyFilters('checkout_submit_form', true, checkoutData, this)) {
            this.getForm().submit();
        }
    }

    shipToDifferentAddressChecked() {
        if ($('[name="ship_to_different_address"]')?.length) {
            return $('[name="ship_to_different_address"]').is(':checked');
        }
        return false;
    }

    getPaymentType() {
        return 'checkout';
    }

    isValidationEnabled() {
        return this.settings.checkoutValidationEnabled;
    }

    isSavedPaymentMethodSelected() {
        const selector = `[name="wc-${this.id}-payment-token"]:checked`;
        return this.isPaymentGatewaySelected() &&
            $(selector).length > 0 && $(selector).val() !== 'new';
    }

    apiFormValidation() {
        const formData = {...this.convertFormToData(), context: this.context.getPage(), payment_method: this.id};
        return apiFetch({
            method: 'POST',
            url: getRestRoute('checkout/validation'),
            data: formData
        }).catch(error => {
            throw error;
        });
    }

    onCheckoutError() {
        if (this.isPaymentGatewaySelected()) {
            this.enablePlaceOrderButton();
            if (this.isReadyToCheckout()) {
                this.displayPaymentReadyMessage();
            }
            this.actions.doAction(`checkout_error_${this.gateway.id}`, this);
        }
    }

    enablePlaceOrderButton() {
        this.getPlaceOrderButton().prop('disabled', false);
    }

}

export {CheckoutController};
export default CheckoutController;